import { createContext, useContext, useState, useEffect } from 'react';
import { authAPI } from '@/services/api';

const AuthContext = createContext(null);

function safeGet(key) {
  try { return localStorage.getItem(key); } catch { return null; }
}
function safeSet(key, value) {
  try { localStorage.setItem(key, value); } catch {}
}
function safeRemove(key) {
  try { localStorage.removeItem(key); } catch {}
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = safeGet('token');
    const savedUser = safeGet('user');
    if (token && savedUser) {
      try {
        setUser(JSON.parse(savedUser));
      } catch {
        safeRemove('user');
        setUser(null);
        setLoading(false);
        return;
      }
      // Verify token is still valid
      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          safeSet('user', JSON.stringify(res.data));
        })
        .catch(() => {
          safeRemove('token');
          safeRemove('user');
          setUser(null);
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, []);

  const login = async (email, password) => {
    const res = await authAPI.login({ email, password });
    if (res.data.requires2FA) {
      // Store temp token and indicate 2FA is needed
      safeSet('tempToken', res.data.tempToken);
      return { requires2FA: true, tempToken: res.data.tempToken };
    }
    const { token, ...userData } = res.data;
    safeSet('token', token);
    safeSet('user', JSON.stringify(userData));
    safeRemove('tempToken');
    setUser(userData);
    return userData;
  };

  const adminLogin = async (email, password) => {
    const res = await authAPI.adminLogin({ email, password });
    const { token, ...userData } = res.data;
    safeSet('token', token);
    safeSet('user', JSON.stringify(userData));
    safeRemove('tempToken');
    setUser(userData);
    return userData;
  };

  const verify2FA = async (token) => {
    const tempToken = safeGet('tempToken');
    if (!tempToken) throw new Error('No 2FA session found');
    const res = await authAPI.verify2FALogin(tempToken, token);
    const { token: authToken, ...userData } = res.data;
    safeSet('token', authToken);
    safeSet('user', JSON.stringify(userData));
    safeRemove('tempToken');
    setUser(userData);
    return userData;
  };

  const register = async (name, email, password, organizationName) => {
    const res = await authAPI.register({ name, email, password, organizationName });
    const { token, ...userData } = res.data;
    safeSet('token', token);
    safeSet('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const googleLogin = async (idToken) => {
    const res = await authAPI.googleLogin(idToken);
    const { token, ...userData } = res.data;
    safeSet('token', token);
    safeSet('user', JSON.stringify(userData));
    setUser(userData);
    return userData;
  };

  const logout = () => {
    safeRemove('token');
    safeRemove('user');
    setUser(null);
  };

  const updateUser = (userData) => {
    setUser(userData);
    safeSet('user', JSON.stringify(userData));
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, adminLogin, verify2FA, register, googleLogin, logout, updateUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
